import { useEffect } from "react";
import { MarketingLayout } from "../components/MarketingLayout";

export const PrivacyPage = () => {
  // Page title & SEO handled globally by useSEO (utils/seo.js)
  return (
    <MarketingLayout>
      <style>{`
        .legal-page { max-width:720px; margin:0 auto; padding:64px 24px 80px; }
        .legal-page h1 { font-family:'Lora',Georgia,serif; font-size:32px; font-weight:600; margin:0 0 8px; }
        .legal-page .updated { font-size:14px; color:#6f6f64; margin-bottom:32px; }
        .legal-page h2 { font-family:'Lora',Georgia,serif; font-size:20px; font-weight:600; margin:40px 0 12px; }
        .legal-page p, .legal-page li { font-size:15px; color:#3f3f46; line-height:1.75; }
        .legal-page ul { padding-left:20px; }
        .legal-page li { margin-bottom:6px; }
        .legal-page a { color:#3F7D52; text-decoration:none; }
        .legal-page a:hover { text-decoration:underline; }
        .legal-page .intro { background:#F5F1E6; border:1px solid #E7DCC7; border-radius:12px; padding:16px 18px; margin-bottom:8px; }
        .legal-page .intro strong { color:#14432B; }
        .legal-page .copyright { font-size:13px; color:#8a8a7e; margin-top:24px; padding-top:24px; border-top:1px solid #ECE8DC; }
      `}</style>
      <div className="legal-page">
        <h1>Privacy Policy</h1>
        <p className="updated">Last updated: February 2, 2026</p>

        <div className="intro">
          <p style={{margin:0}}>This Privacy Policy is designed to comply with the <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong> and applicable Canadian provincial privacy legislation. Tally is committed to protecting your personal information and being transparent about how we collect, use, and disclose it.</p>
        </div>

        <h2>1. Accountability</h2>
        <p>Tally Technologies Inc. ("Tally," "we," "us," or "our") is responsible for personal information under our control. We have designated a Privacy Officer who is accountable for our compliance with this Privacy Policy and PIPEDA.</p>
        <p><strong>Privacy Officer Contact:</strong><br />Email: <a href="mailto:hello@usetally.ca">hello@usetally.ca</a><br />Address: Toronto, Ontario, Canada</p>

        <h2>2. Information We Collect</h2>
        <p>We collect the following types of personal information:</p>
        <ul>
          <li><strong>Account Information:</strong> Name, email address, date of birth, and password when you create an account.</li>
          <li><strong>Financial Information:</strong> When you connect your bank accounts through Plaid, we receive transaction data, account balances, and account identifiers. We do not store your bank login credentials.</li>
          <li><strong>Usage Information:</strong> How you interact with Tally, including pages viewed, features used, and preferences set.</li>
          <li><strong>Device Information:</strong> Browser type, IP address, device identifiers, and operating system.</li>
          <li><strong>Payment Information:</strong> If you subscribe to Tally Power, payment details are processed by Stripe. We do not store full credit card numbers.</li>
        </ul>

        <h2>3. Purposes for Collection, Use, and Disclosure</h2>
        <p>We collect, use, and disclose your personal information for the following purposes:</p>
        <ul>
          <li>To provide and maintain our budgeting and financial tracking services</li>
          <li>To analyze your transactions and provide personalized financial insights</li>
          <li>To detect recurring bills and subscriptions</li>
          <li>To send you notifications about upcoming bills and budget alerts</li>
          <li>To process subscription payments</li>
          <li>To respond to your inquiries and provide customer support</li>
          <li>To improve our services and develop new features</li>
          <li>To comply with legal obligations</li>
        </ul>

        <h2>4. Consent</h2>
        <p>We obtain your consent before collecting, using, or disclosing your personal information, except where permitted or required by law. By creating an account and using Tally, you consent to the collection, use, and disclosure of your personal information as described in this Privacy Policy.</p>
        <p>You may withdraw your consent at any time by contacting us or deleting your account. However, withdrawing consent may limit your ability to use certain features of Tally.</p>
        <p>When you connect your bank accounts through Plaid, you provide separate consent to Plaid to access your financial data. Please review <a href="https://plaid.com/legal/" target="_blank" rel="noopener noreferrer">Plaid's Privacy Policy</a> for more information.</p>

        <h2>5. Limiting Collection and Retention</h2>
        <p>We only collect personal information that is necessary for the purposes identified. We retain your personal information only as long as necessary to fulfill those purposes or as required by law.</p>
        <p>When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal, regulatory, or legitimate business purposes.</p>

        <h2>6. Safeguards</h2>
        <p>We protect your personal information using appropriate security measures, including:</p>
        <ul>
          <li>Encryption of data in transit (TLS/SSL) and at rest</li>
          <li>Secure authentication and access controls</li>
          <li>Regular security assessments and monitoring</li>
          <li>Employee training on privacy and security practices</li>
          <li>Third-party service providers with strong security standards (Plaid, Stripe)</li>
        </ul>
        <p>We never store your bank login credentials. Bank connections are securely managed through Plaid, a leading financial data provider trusted by thousands of financial institutions.</p>

        <h2>7. Third-Party Service Providers</h2>
        <p>We share your personal information with the following third-party service providers:</p>
        <ul>
          <li><strong>Plaid Inc.</strong> – To securely connect your bank accounts and retrieve financial data</li>
          <li><strong>Stripe Inc.</strong> – To process subscription payments</li>
          <li><strong>OpenAI</strong> – To provide AI-powered financial insights (anonymized data only)</li>
          <li><strong>Cloud hosting providers</strong> – To host and operate our services</li>
        </ul>
        <p>We require all third-party service providers to protect your personal information and use it only for the purposes for which it was disclosed.</p>

        <h2>8. Your Rights Under PIPEDA</h2>
        <p>Under PIPEDA, you have the right to:</p>
        <ul>
          <li><strong>Access</strong> your personal information held by Tally</li>
          <li><strong>Correct</strong> any inaccurate or incomplete information</li>
          <li><strong>Withdraw consent</strong> for the collection, use, or disclosure of your information</li>
          <li><strong>Request deletion</strong> of your personal information</li>
          <li><strong>File a complaint</strong> with the Office of the Privacy Commissioner of Canada</li>
        </ul>
        <p>To exercise any of these rights, please contact our Privacy Officer at <a href="mailto:privacy@tally.ca">privacy@tally.ca</a>. We will respond to your request within 30 days.</p>

        <h2>9. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.</p>

        <h2>10. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
        <p><strong>Tally Technologies Inc.</strong><br />Privacy Officer<br />Email: <a href="mailto:hello@usetally.ca">hello@usetally.ca</a><br />Toronto, Ontario, Canada</p>
        <p>You may also file a complaint with the <a href="https://www.priv.gc.ca/" target="_blank" rel="noopener noreferrer">Office of the Privacy Commissioner of Canada</a>.</p>

        <p className="copyright">© 2026 Tally Technologies Inc. All rights reserved.</p>
      </div>
    </MarketingLayout>
  );
};
export default PrivacyPage;
